// Date-fns locales are small, so we keep them all statically imported

import type { Locale } from 'date-fns';
import { cs } from 'date-fns/locale/cs';
import { de } from 'date-fns/locale/de';
import { enUS } from 'date-fns/locale/en-US';
import { es } from 'date-fns/locale/es';
import { fi } from 'date-fns/locale/fi';
import { fr } from 'date-fns/locale/fr';
import { frCA } from 'date-fns/locale/fr-CA';
import { hr } from 'date-fns/locale/hr';
import { hu } from 'date-fns/locale/hu';
import { it } from 'date-fns/locale/it';
import { ja } from 'date-fns/locale/ja';
import { ko } from 'date-fns/locale/ko';
import { ms } from 'date-fns/locale/ms';
import { nl } from 'date-fns/locale/nl';
import { pl } from 'date-fns/locale/pl';
import { pt } from 'date-fns/locale/pt';
import { ptBR } from 'date-fns/locale/pt-BR';
import { ro } from 'date-fns/locale/ro';
import { sk } from 'date-fns/locale/sk';
import { srLatn } from 'date-fns/locale/sr-Latn';
import { sv } from 'date-fns/locale/sv';
import { zhCN } from 'date-fns/locale/zh-CN';
import { zhTW } from 'date-fns/locale/zh-TW';
import type { InitOptions } from 'i18next';
import { z } from 'zod';

// Only English and German translations are statically imported (others loaded dynamically)
import defaultGermanTranslation from './resources/de-DE.json' with { type: 'json' };
import defaultEnglishTranslation from './resources/en-US.json' with { type: 'json' };
import type { TranslationResource } from './translation';
import { translationResourceOverrideSchema } from './translation';

export const languageKeySchema = z.union([
    z.literal('en-US'),
    z.literal('cs-CZ'),
    z.literal('de-DE'),
    z.literal('es-ES'),
    z.literal('es-MX'),
    z.literal('fi-FI'),
    z.literal('fr-CA'),
    z.literal('fr-FR'),
    z.literal('hr-HR'),
    z.literal('hu-HU'),
    z.literal('it-IT'),
    z.literal('ja-JP'),
    z.literal('nl-NL'),
    z.literal('pl-PL'),
    z.literal('pt-BR'),
    z.literal('pt-PT'),
    z.literal('ro-RO'),
    z.literal('sk-SK'),
    z.literal('sr-RS'),
    z.literal('zh-CN'),
    z.literal('zh-TW'),
    z.literal('ko-KR'),
    z.literal('ms-MY'),
    z.literal('sv-SE'),
]);

export type LanguageKey = z.infer<typeof languageKeySchema>;

export const languageCodeSchemaLegacy = z.union([
    z.literal('cs-CZ'),
    z.literal('de-DE'),
    z.literal('en-US'),
    z.literal('es-ES'),
    z.literal('es-MX'),
    z.literal('fi-FI'),
    z.literal('fr-CA'),
    z.literal('fr-FR'),
    z.literal('hr-HR'),
    z.literal('hu-HU'),
    z.literal('it-IT'),
    z.literal('ja-JP'),
    z.literal('nl-NL'),
    z.literal('pl-PL'),
    z.literal('pt-BR'),
    z.literal('pt-PT'),
    z.literal('ro-RO'),
    z.literal('sk-SK'),
    z.literal('sr-RS'),
    z.literal('zh-CN'),
    z.literal('zh-TW'),
    z.literal('ko-KR'),
    z.literal('ms-MY'),
    z.literal('sv-SE'),
]);
export const languageCodeSchema = languageCodeSchemaLegacy.catch('en-US');

export type LanguageCode = z.infer<typeof languageCodeSchema>;

/**
 * Maps short language codes to full locale codes.
 * Used for backwards compatibility when parsing data that may have old short codes.
 */
const shortToFullLocaleMap: Record<string, LanguageKey> = {
    cs: 'cs-CZ',
    de: 'de-DE',
    en: 'en-US',
    es: 'es-ES',
    fi: 'fi-FI',
    fr: 'fr-FR',
    hr: 'hr-HR',
    hu: 'hu-HU',
    it: 'it-IT',
    ja: 'ja-JP',
    ko: 'ko-KR',
    ms: 'ms-MY',
    nl: 'nl-NL',
    pl: 'pl-PL',
    pt: 'pt-PT',
    ro: 'ro-RO',
    sk: 'sk-SK',
    sr: 'sr-RS',
    sv: 'sv-SE',
    zh: 'zh-CN',
    'zh-TW': 'zh-TW',
};

/**
 * Schema that accepts both short codes (en, de, etc.) and full locale codes (en-US, de-DE, etc.),
 * and transforms short codes to full locale codes.
 */

export const languageCodeWithLegacySchema = z
    .string()
    .transform((code): LanguageCode => {
        const fullLocale = shortToFullLocaleMap[code];

        if (fullLocale !== undefined) {
            return fullLocale;
        }

        return code as LanguageCode;
    })
    .pipe(languageCodeSchema);

/**
 * Transforms an object with legacy short language keys (en, de) to full locale keys (en-US, de-DE).
 */
const transformLegacyKeys = <T>(obj: Record<string, T>): Partial<Record<LanguageKey, T>> => {
    const result: Partial<Record<LanguageKey, T>> = {};

    for (const [key, value] of Object.entries(obj)) {
        const fullKey = shortToFullLocaleMap[key] ?? key;

        // Only include valid language keys
        if (languageKeySchema.safeParse(fullKey).success) {
            result[fullKey as LanguageKey] = value;
        }
    }

    return result;
};

export const translationOverridesParameterSchema = z
    .record(z.string(), translationResourceOverrideSchema)
    .transform(transformLegacyKeys);

export type TranslationOverrides = z.infer<typeof translationOverridesParameterSchema>;

export const languageCodeSchemaForJSA = z.string();

export type LanguageCodeForJSA = z.infer<typeof languageCodeSchemaForJSA>;

export type LanguageConfig = {
    readonly code: LanguageCode;
    readonly date: {
        readonly format: string;
        readonly locale: Locale;
        readonly mask: string;
        readonly placeholder: string;
    };
    readonly name: string;
};

export type LanguageConfigMap = Partial<Record<LanguageKey, LanguageConfig>> & { readonly 'en-US': LanguageConfig };

// All language configs are available statically (date-fns locales are small)
// Only translation JSON files are loaded dynamically for es-ES, fr-FR, ja-JP
export const availableI18nLanguages: LanguageConfigMap = {
    'cs-CZ': {
        code: 'cs-CZ',
        date: {
            format: 'dd.MM.yyyy',
            locale: cs,
            mask: '__.__.____',
            placeholder: 'dd.mm.rrrr',
        },
        name: 'Čeština',
    },
    'de-DE': {
        code: 'de-DE',
        date: {
            format: 'dd.MM.yyyy',
            locale: de,
            mask: '__.__.____',
            placeholder: 'tt.mm.jjjj',
        },
        name: 'Deutsch',
    },
    'en-US': {
        code: 'en-US',
        date: {
            format: 'MM/dd/yyyy',
            locale: enUS,
            mask: '__/__/____',
            placeholder: 'mm/dd/yyyy',
        },
        name: 'English',
    },
    'es-ES': {
        code: 'es-ES',
        date: {
            format: 'dd/MM/yyyy',
            locale: es,
            mask: '__/__/____',
            placeholder: 'dd/mm/yyyy',
        },
        name: 'Español',
    },
    'es-MX': {
        code: 'es-MX',
        date: {
            format: 'dd/MM/yyyy',
            locale: es,
            mask: '__/__/____',
            placeholder: 'dd/mm/aaaa',
        },
        name: 'Español (México)',
    },
    'fi-FI': {
        code: 'fi-FI',
        date: {
            format: 'dd.MM.yyyy',
            locale: fi,
            mask: '__.__.____',
            placeholder: 'pp.kk.vvvv',
        },
        name: 'Suomi',
    },
    'fr-CA': {
        code: 'fr-CA',
        date: {
            format: 'yyyy-MM-dd',
            locale: frCA,
            mask: '____-__-__',
            placeholder: 'aaaa-mm-jj',
        },
        name: 'Français (Canada)',
    },
    'fr-FR': {
        code: 'fr-FR',
        date: {
            format: 'dd/MM/yyyy',
            locale: fr,
            mask: '__/__/____',
            placeholder: 'jj/mm/aaaa',
        },
        name: 'Français',
    },
    'hr-HR': {
        code: 'hr-HR',
        date: {
            format: 'dd.MM.yyyy',
            locale: hr,
            mask: '__.__.____',
            placeholder: 'dd.mm.gggg',
        },
        name: 'Hrvatski',
    },
    'hu-HU': {
        code: 'hu-HU',
        date: {
            format: 'yyyy.MM.dd',
            locale: hu,
            mask: '____.__.__',
            placeholder: 'éééé.hh.nn',
        },
        name: 'Magyar',
    },
    'it-IT': {
        code: 'it-IT',
        date: {
            format: 'dd/MM/yyyy',
            locale: it,
            mask: '__/__/____',
            placeholder: 'gg/mm/aaaa',
        },
        name: 'Italiano',
    },
    'ja-JP': {
        code: 'ja-JP',
        date: {
            format: 'yyyy/MM/dd',
            locale: ja,
            mask: '____/__/__',
            placeholder: 'yyyy/mm/dd',
        },
        name: '日本語',
    },
    'ko-KR': {
        code: 'ko-KR',
        date: {
            format: 'yyyy.MM.dd',
            locale: ko,
            mask: '____.__.__',
            placeholder: 'yyyy.mm.dd',
        },
        name: '한국어',
    },
    'ms-MY': {
        code: 'ms-MY',
        date: {
            format: 'dd/MM/yyyy',
            locale: ms,
            mask: '__/__/____',
            placeholder: 'dd/mm/yyyy',
        },
        name: 'Bahasa Melayu',
    },
    'nl-NL': {
        code: 'nl-NL',
        date: {
            format: 'dd-MM-yyyy',
            locale: nl,
            mask: '__-__-____',
            placeholder: 'dd-mm-jjjj',
        },
        name: 'Nederlands',
    },
    'pl-PL': {
        code: 'pl-PL',
        date: {
            format: 'dd.MM.yyyy',
            locale: pl,
            mask: '__.__.____',
            placeholder: 'dd.mm.rrrr',
        },
        name: 'Polski',
    },
    'pt-BR': {
        code: 'pt-BR',
        date: {
            format: 'dd/MM/yyyy',
            locale: ptBR,
            mask: '__/__/____',
            placeholder: 'dd/mm/aaaa',
        },
        name: 'Português (Brasil)',
    },
    'pt-PT': {
        code: 'pt-PT',
        date: {
            format: 'dd/MM/yyyy',
            locale: pt,
            mask: '__/__/____',
            placeholder: 'dd/mm/aaaa',
        },
        name: 'Português',
    },
    'ro-RO': {
        code: 'ro-RO',
        date: {
            format: 'dd.MM.yyyy',
            locale: ro,
            mask: '__.__.____',
            placeholder: 'zz.ll.aaaa',
        },
        name: 'Română',
    },
    'sk-SK': {
        code: 'sk-SK',
        date: {
            format: 'dd.MM.yyyy',
            locale: sk,
            mask: '__.__.____',
            placeholder: 'dd.mm.rrrr',
        },
        name: 'Slovenčina',
    },
    'sr-RS': {
        code: 'sr-RS',
        date: {
            format: 'dd.MM.yyyy',
            locale: srLatn,
            mask: '__.__.____',
            placeholder: 'dd.mm.gggg',
        },
        name: 'Srpski',
    },
    'sv-SE': {
        code: 'sv-SE',
        date: {
            format: 'yyyy-MM-dd',
            locale: sv,
            mask: '____-__-__',
            placeholder: 'åååå-mm-dd',
        },
        name: 'Svenska',
    },
    'zh-CN': {
        code: 'zh-CN',
        date: {
            format: 'yyyy/MM/dd',
            locale: zhCN,
            mask: '____/__/__',
            placeholder: 'yyyy/mm/dd',
        },
        name: '简体中文',
    },
    'zh-TW': {
        code: 'zh-TW',
        date: {
            format: 'yyyy/MM/dd',
            locale: zhTW,
            mask: '____/__/__',
            placeholder: 'yyyy/mm/dd',
        },
        name: '繁體中文',
    },
};

export const isAvailableLanguage = (language: string | undefined): language is LanguageKey => {
    return languageKeySchema.safeParse(language).success;
};

type ResourcesConfiguration = Pick<
    Record<LanguageKey, { readonly translation: TranslationResource }>,
    'de-DE' | 'en-US'
>;

export type Internationalization = {
    readonly overrideTranslation: (initializeWithOverrides: TranslationOverrides) => void;
};

// Only English and German are bundled statically
export const defaultResources: ResourcesConfiguration = {
    'de-DE': {
        translation: defaultGermanTranslation satisfies TranslationResource,
    },
    'en-US': {
        translation: defaultEnglishTranslation satisfies TranslationResource,
    },
};

/**
 * Loads translation resources dynamically.
 * English and German translations are returned immediately (statically imported).
 * Other languages are loaded at runtime with a fallback to English on failure.
 */
export const loadTranslationResource = async (language: LanguageKey): Promise<TranslationResource> => {
    switch (language) {
        case 'en-US':
            return defaultEnglishTranslation;
        case 'de-DE':
            return defaultGermanTranslation;
        default:
            break;
    }

    try {
        const loadJsonTranslation = async (
            loader: () => Promise<{ readonly default: unknown }>,
        ): Promise<TranslationResource> => {
            const translation = await loader();

            return translation.default as TranslationResource;
        };

        switch (language) {
            case 'es-ES': {
                return loadJsonTranslation(() => import('./resources/es-ES.json', { with: { type: 'json' } }));
            }
            case 'es-MX': {
                return loadJsonTranslation(() => import('./resources/es-MX.json', { with: { type: 'json' } }));
            }
            case 'fi-FI': {
                return loadJsonTranslation(() => import('./resources/fi-FI.json', { with: { type: 'json' } }));
            }
            case 'cs-CZ': {
                return loadJsonTranslation(() => import('./resources/cs-CZ.json', { with: { type: 'json' } }));
            }
            case 'fr-CA': {
                return loadJsonTranslation(() => import('./resources/fr-CA.json', { with: { type: 'json' } }));
            }
            case 'fr-FR': {
                return loadJsonTranslation(() => import('./resources/fr-FR.json', { with: { type: 'json' } }));
            }
            case 'hr-HR': {
                return loadJsonTranslation(() => import('./resources/hr-HR.json', { with: { type: 'json' } }));
            }
            case 'hu-HU': {
                return loadJsonTranslation(() => import('./resources/hu-HU.json', { with: { type: 'json' } }));
            }
            case 'it-IT': {
                return loadJsonTranslation(() => import('./resources/it-IT.json', { with: { type: 'json' } }));
            }
            case 'ja-JP': {
                return loadJsonTranslation(() => import('./resources/ja-JP.json', { with: { type: 'json' } }));
            }
            case 'nl-NL': {
                return loadJsonTranslation(() => import('./resources/nl-NL.json', { with: { type: 'json' } }));
            }
            case 'pt-BR': {
                return loadJsonTranslation(() => import('./resources/pt-BR.json', { with: { type: 'json' } }));
            }
            case 'pt-PT': {
                return loadJsonTranslation(() => import('./resources/pt-PT.json', { with: { type: 'json' } }));
            }
            case 'ro-RO': {
                return loadJsonTranslation(() => import('./resources/ro-RO.json', { with: { type: 'json' } }));
            }
            case 'sk-SK': {
                return loadJsonTranslation(() => import('./resources/sk-SK.json', { with: { type: 'json' } }));
            }
            case 'sr-RS': {
                return loadJsonTranslation(() => import('./resources/sr-RS.json', { with: { type: 'json' } }));
            }
            case 'zh-CN': {
                return loadJsonTranslation(() => import('./resources/zh-CN.json', { with: { type: 'json' } }));
            }
            case 'zh-TW': {
                return loadJsonTranslation(() => import('./resources/zh-TW.json', { with: { type: 'json' } }));
            }
            case 'ko-KR': {
                return loadJsonTranslation(() => import('./resources/ko-KR.json', { with: { type: 'json' } }));
            }
            case 'ms-MY': {
                return loadJsonTranslation(() => import('./resources/ms-MY.json', { with: { type: 'json' } }));
            }
            case 'pl-PL': {
                return loadJsonTranslation(() => import('./resources/pl-PL.json', { with: { type: 'json' } }));
            }
            case 'sv-SE': {
                return loadJsonTranslation(() => import('./resources/sv-SE.json', { with: { type: 'json' } }));
            }
            default:
                return defaultEnglishTranslation;
        }
    } catch {
        // Failed to load translation resource, falling back to English

        return defaultEnglishTranslation;
    }
};

export const i18nDefaultConfig: InitOptions = {
    fallbackLng: 'en-US',
    interpolation: { escapeValue: false },
    load: 'currentOnly',
    ns: ['translation'],
    partialBundledLanguages: true,
    resources: defaultResources,
    returnNull: false,
    supportedLngs: [
        'en-US',
        'cs-CZ',
        'de-DE',
        'es-ES',
        'es-MX',
        'fi-FI',
        'fr-CA',
        'fr-FR',
        'hr-HR',
        'hu-HU',
        'it-IT',
        'ja-JP',
        'nl-NL',
        'pl-PL',
        'pt-BR',
        'pt-PT',
        'ro-RO',
        'sk-SK',
        'sr-RS',
        'zh-CN',
        'zh-TW',
        'ko-KR',
        'ms-MY',
        'sv-SE',
    ],
};
